/**
 * Tenant Scenarios, Five Early-Validation Logos from ClearEqual Investor Deck
 *
 * Each tenant is a fully realized demo scenario with internally-consistent data
 * that tells a specific story during an investor pitch. All numbers are
 * plausible given the company profile (industry, size, geography).
 *
 * Narrative roles:
 *   - global-consultancy-de  → "preventive" / already compliant
 *   - consumer-goods-de      → "crisis" / triggers joint pay assessment (>5%, >6mo)
 *   - global-bank-es         → "scale" / complex multi-entity org
 *   - global-manufacturer-ch → "AI insight" / clear division-level pattern
 *   - global-security-uk     → "international expansion" / preparing for UK equivalent
 */

const MONTHS = [
  'May 2025', 'Jun 2025', 'Jul 2025', 'Aug 2025', 'Sep 2025', 'Oct 2025',
  'Nov 2025', 'Dec 2025', 'Jan 2026', 'Feb 2026', 'Mar 2026', 'Apr 2026',
];

/**
 * Helper: build a monthly trend series from a starting value that drifts
 * toward a target value with realistic noise.
 */
const trend = (start, end, noise = 0.25) => {
  const step = (end - start) / (MONTHS.length - 1);
  return MONTHS.map((month, i) => {
    const base = start + step * i;
    const jitter = (Math.sin(i * 1.7) + Math.cos(i * 0.9)) * noise;
    const overall = +(base + jitter).toFixed(1);
    // Median typically runs 1–2 points below mean
    const median = +(overall - 1.3 - Math.abs(Math.sin(i)) * 0.4).toFixed(1);
    return { month, overall, median };
  });
};

export const TENANTS = {
  'clearequal-demo': {
    id: 'clearequal-demo',
    name: 'ClearEqual',
    shortName: 'ClearEqual Demo',
    country: 'Belgium',
    countryCode: 'BE',
    industry: 'HR Technology',
    employees: 524,
    narrativeRole: 'sandbox',
    narrativeTagline: 'Our internal sandbox for product validation and customer demos.',
    reportingYear: 2026,

    metrics: {
      meanPayGap: 1.8,
      meanPayGapDelta: -0.6,
      medianPayGap: 1.2,
      medianPayGapDelta: -0.3,
      variableMeanGap: 2.4,
      variableMedianGap: 1.7,
      variableRecipientsFemale: 71,
      variableRecipientsMale: 73,
      complianceScore: 96,
      activeAssessments: 0,
      totalWorkers: 524,
      femaleShare: 47.8,
    },

    categoriesOfWorkers: [
      { name: 'Engineering',          meanGap: 1.6, medianGap: 1.0, headcount: 168, femaleShare: 38 },
      { name: 'Product',              meanGap: 1.2, medianGap: 0.8, headcount:  62, femaleShare: 51 },
      { name: 'Sales',                meanGap: 2.4, medianGap: 1.8, headcount:  94, femaleShare: 44 },
      { name: 'Marketing',            meanGap: 1.4, medianGap: 0.9, headcount:  48, femaleShare: 62 },
      { name: 'Customer Success',     meanGap: 1.1, medianGap: 0.6, headcount:  72, femaleShare: 58 },
      { name: 'Operations & Finance', meanGap: 2.1, medianGap: 1.5, headcount:  54, femaleShare: 52 },
      { name: 'Legal & Compliance',   meanGap: 0.9, medianGap: 0.4, headcount:  18, femaleShare: 56 },
      { name: 'People & G&A',         meanGap: 1.0, medianGap: 0.7, headcount:   8, femaleShare: 63 },
    ],

    quartiles: [
      { name: 'Q1 (Lowest)',  femalePct: 54, malePct: 46, headcount: 131 },
      { name: 'Q2',           femalePct: 51, malePct: 49, headcount: 131 },
      { name: 'Q3',           femalePct: 46, malePct: 54, headcount: 131 },
      { name: 'Q4 (Highest)', femalePct: 41, malePct: 59, headcount: 131 },
    ],

    trendData: trend(2.4, 1.8, 0.15),

    assessmentAlerts: [],

    aiInsights: {
      headline: 'Mean gap of 1.8% places ClearEqual well below the Article 10 threshold and in the leading decile of EU HR-tech companies.',
      body: 'A 60-basis-point contraction over the trailing twelve months reflects deliberate hiring discipline and pay-band rigour applied since the 2024 reset. Female representation in Q4 (highest pay) has improved from 36% to 41% over the reporting period, driven by senior engineering and sales hires. The compensation methodology is transparent across all eight worker categories, with the largest residual variance in Sales (2.4%) attributable to recent senior hires whose variable components have not yet vested. No Article 10 obligations are engaged. The organisation is positioned to maintain compliance through the 7 June 2026 directive deadline without remedial measures.',
      recommendations: [
        'Continue the quarterly internal review cadence; no joint pay assessment is required under Article 10 at this reporting period.',
        'Monitor the Sales variable-component vesting cycle to confirm the trajectory holds as the recent cohort completes their first performance period.',
        'Consider publishing the methodology externally as a thought-leadership signal, given the strong audit-readiness posture.',
        'Maintain the bi-annual pay band recalibration to preserve compression discipline as headcount grows.',
      ],
    },
    aiInsightsDe: {
      headline: 'Mit einem mittleren Pay-Gap von 1,8 % liegt ClearEqual deutlich unterhalb der Schwelle nach Artikel 10 und im führenden Dezil der EU-HR-Tech-Unternehmen.',
      body: 'Die Verringerung um 60 Basispunkte über die letzten zwölf Monate spiegelt eine bewusste Einstellungsdisziplin und konsequente Vergütungsbandführung seit dem Reset 2024 wider. Der Frauenanteil im Q4 (höchste Vergütung) hat sich im Berichtszeitraum von 36 % auf 41 % verbessert, getragen von Senior-Hires in Engineering und Sales. Die Vergütungsmethodik ist über alle acht Mitarbeitendenkategorien transparent; die größte verbleibende Varianz besteht im Vertrieb (2,4 %) und ist auf jüngste Senior-Einstellungen zurückzuführen, deren variable Komponenten noch nicht vollständig vested sind. Pflichten nach Artikel 10 sind nicht ausgelöst. Die Organisation ist positioniert, die Compliance bis zur Richtlinienfrist 7. Juni 2026 ohne Abhilfemaßnahmen aufrechtzuerhalten.',
      recommendations: [
        'Den vierteljährlichen internen Überprüfungsrhythmus fortführen; eine gemeinsame Entgeltbewertung nach Artikel 10 ist im aktuellen Berichtszeitraum nicht erforderlich.',
        'Den Vesting-Zyklus der variablen Vergütung im Vertrieb beobachten, um die Entwicklung zu bestätigen, sobald die jüngste Kohorte ihre erste Bewertungsperiode abschließt.',
        'Die Veröffentlichung der Methodik als Thought-Leadership-Signal in Erwägung ziehen, angesichts der robusten Auditbereitschaft.',
        'Die halbjährliche Neukalibrierung der Vergütungsbänder beibehalten, um die Spreizungsdisziplin bei wachsender Personalstärke zu sichern.',
      ],
    },
  },

  'global-consultancy-de': {
    id: 'global-consultancy-de',
    name: 'Global Consultancy',
    shortName: 'Global Consultancy DE',
    country: 'Germany',
    countryCode: 'DE',
    industry: 'Professional Services',
    employees: 8240,
    narrativeRole: 'preventive',
    narrativeTagline: 'Ahead of the directive, using ClearEqual to stay that way.',
    reportingYear: 2025,

    // Art. 9 core metrics
    metrics: {
      meanPayGap: 3.2,            // (a) mean gender pay gap
      meanPayGapDelta: -0.4,      // vs. prior period
      medianPayGap: 2.1,          // (c) median gender pay gap
      medianPayGapDelta: -0.2,
      variableMeanGap: 4.8,       // (b) mean gap in variable components
      variableMedianGap: 3.1,     // (d) median gap in variable components
      variableRecipientsFemale: 68, // (e) % of female workers receiving variable
      variableRecipientsMale: 74,   // (e) % of male workers receiving variable
      complianceScore: 94,          // proprietary composite
      activeAssessments: 0,
      totalWorkers: 8240,
      femaleShare: 46.2,
    },

    // (g) Gender pay gap by categories of workers
    categoriesOfWorkers: [
      { name: 'Senior Partners',   meanGap: 2.8, medianGap: 1.9, headcount: 62,   femaleShare: 34 },
      { name: 'Partners',          meanGap: 3.1, medianGap: 2.4, headcount: 184,  femaleShare: 38 },
      { name: 'Principals',        meanGap: 2.9, medianGap: 2.0, headcount: 412,  femaleShare: 42 },
      { name: 'Senior Managers',   meanGap: 3.4, medianGap: 2.2, headcount: 980,  femaleShare: 45 },
      { name: 'Managers',          meanGap: 3.0, medianGap: 1.8, headcount: 1820, femaleShare: 48 },
      { name: 'Senior Consultants',meanGap: 2.7, medianGap: 1.5, headcount: 2340, femaleShare: 49 },
      { name: 'Consultants',       meanGap: 2.1, medianGap: 1.2, headcount: 1960, femaleShare: 51 },
      { name: 'Business Services', meanGap: 4.2, medianGap: 3.1, headcount: 482,  femaleShare: 64 },
    ],

    // (f) Quartile pay band distribution
    quartiles: [
      { name: 'Q1 (Lowest)',  femalePct: 52, malePct: 48, headcount: 2060 },
      { name: 'Q2',           femalePct: 49, malePct: 51, headcount: 2060 },
      { name: 'Q3',           femalePct: 45, malePct: 55, headcount: 2060 },
      { name: 'Q4 (Highest)', femalePct: 39, malePct: 61, headcount: 2060 },
    ],

    trendData: trend(4.1, 3.2, 0.2),

    assessmentAlerts: [
      {
        id: 'ASS-2025-Q3-001',
        title: 'Preventive review, Business Services',
        description: 'Proactive check on support functions where variable comp gap trends slightly above department average.',
        department: 'Business Services',
        gapPercentage: 4.2,
        priority: 'low',
        dueDate: '2025-11-30',
        status: 'scheduled',
      },
    ],

    aiInsights: {
      headline: 'Mean gap of 3.2% places the organisation in the upper decile of professional services firms tracked under Directive 2023/970.',
      body: 'A 40-basis-point contraction over the trailing twelve months is attributable to promotion velocity at the Senior Consultant grade, where female representation has reached 49% and intake parity has been sustained for six consecutive quarters. The single area warranting continued observation is Business Services (mean 4.2%, median 3.1%): female concentration of 64% combined with a compressed compensation band has produced a structural variance that, while currently within the directive threshold, lacks downward momentum. No Article 10 obligations are presently engaged.',
      recommendations: [
        'Continue the annual joint pay assessment cadence; no remedial measures required under Article 10 at this reporting period.',
        'Refer the Business Services variable-component allocation methodology to the next compensation committee for structural review.',
        'Consider voluntary external publication of pay equity metrics; reputational positioning supports senior talent acquisition strategy.',
        'Maintain promotion velocity monitoring at the Senior Consultant grade as the principal lever sustaining current trajectory.',
      ],
    },
    aiInsightsDe: {
      headline: 'Mit einem mittleren Pay-Gap von 3,2 % liegt das Unternehmen im obersten Dezil der unter Richtlinie 2023/970 erfassten Beratungsunternehmen.',
      body: 'Die Verringerung um 40 Basispunkte über die letzten zwölf Monate ist auf die Beförderungsdynamik auf der Senior-Consultant-Ebene zurückzuführen, auf der der Frauenanteil 49 % erreicht hat und Eintrittsparität über sechs aufeinanderfolgende Quartale aufrechterhalten wurde. Beobachtungswürdig bleibt allein der Bereich Business Services (Mittelwert 4,2 %, Median 3,1 %): Die Frauenkonzentration von 64 % in Verbindung mit einem komprimierten Vergütungsband hat eine strukturelle Varianz erzeugt, die zwar derzeit innerhalb des Richtlinienschwellenwerts liegt, jedoch keine Abwärtsdynamik aufweist. Pflichten nach Artikel 10 sind gegenwärtig nicht ausgelöst.',
      recommendations: [
        'Die jährliche gemeinsame Entgeltbewertung im bisherigen Rhythmus fortführen; im aktuellen Berichtszeitraum sind keine Abhilfemaßnahmen nach Artikel 10 erforderlich.',
        'Die Methodik zur Zuteilung variabler Vergütungsbestandteile in Business Services dem nächsten Vergütungsausschuss zur strukturellen Überprüfung vorlegen.',
        'Eine freiwillige externe Veröffentlichung der Pay-Equity-Kennzahlen erwägen; die Reputationspositionierung unterstützt die Personalakquisestrategie auf Senior-Ebene.',
        'Die Beförderungsdynamik auf der Senior-Consultant-Ebene weiterhin überwachen, da sie der zentrale Hebel zur Aufrechterhaltung der aktuellen Entwicklung ist.',
      ],
    },
  },

  'consumer-goods-de': {
    id: 'consumer-goods-de',
    name: 'Consumer Goods Group',
    shortName: 'Consumer Goods DE',
    country: 'Germany',
    countryCode: 'DE',
    industry: 'Consumer Goods & Retail',
    employees: 12480,
    narrativeRole: 'crisis',
    narrativeTagline: 'A 7.1% gap that triggered a mandatory joint pay assessment.',
    reportingYear: 2025,

    metrics: {
      meanPayGap: 7.1,
      meanPayGapDelta: +0.6,
      medianPayGap: 5.4,
      medianPayGapDelta: +0.3,
      variableMeanGap: 11.8,
      variableMedianGap: 8.2,
      variableRecipientsFemale: 41,
      variableRecipientsMale: 58,
      complianceScore: 58,
      activeAssessments: 2,
      totalWorkers: 12480,
      femaleShare: 52.8,
    },

    categoriesOfWorkers: [
      { name: 'Executive Leadership', meanGap: 8.4,  medianGap: 6.2, headcount: 42,   femaleShare: 24 },
      { name: 'Senior Management',    meanGap: 9.1,  medianGap: 7.0, headcount: 186,  femaleShare: 31 },
      { name: 'Middle Management',    meanGap: 7.8,  medianGap: 5.9, headcount: 724,  femaleShare: 38 },
      { name: 'Brand & Marketing',    meanGap: 4.2,  medianGap: 3.1, headcount: 980,  femaleShare: 62 },
      { name: 'Sales (Field)',        meanGap: 10.4, medianGap: 8.1, headcount: 2180, femaleShare: 44 },
      { name: 'Supply Chain',         meanGap: 6.8,  medianGap: 5.2, headcount: 1540, femaleShare: 36 },
      { name: 'Manufacturing',        meanGap: 5.9,  medianGap: 4.4, headcount: 3820, femaleShare: 48 },
      { name: 'Retail Operations',    meanGap: 6.2,  medianGap: 4.8, headcount: 3008, femaleShare: 71 },
    ],

    quartiles: [
      { name: 'Q1 (Lowest)',  femalePct: 64, malePct: 36, headcount: 3120 },
      { name: 'Q2',           femalePct: 58, malePct: 42, headcount: 3120 },
      { name: 'Q3',           femalePct: 48, malePct: 52, headcount: 3120 },
      { name: 'Q4 (Highest)', femalePct: 31, malePct: 69, headcount: 3120 },
    ],

    trendData: trend(6.2, 7.1, 0.3),

    assessmentAlerts: [
      {
        id: 'ASS-2025-Q4-018',
        title: 'Mandatory Joint Pay Assessment, Sales (Field)',
        description: 'Mean gap of 10.4% has exceeded the 5% threshold for 8 consecutive months without justified explanation. Joint pay assessment under Art. 10 is required.',
        department: 'Sales (Field)',
        gapPercentage: 10.4,
        priority: 'critical',
        dueDate: '2026-01-15',
        status: 'pending',
      },
      {
        id: 'ASS-2025-Q4-019',
        title: 'Variable Compensation Review, Senior Management',
        description: 'Variable-component gap of 11.8% paired with lower female participation (41% vs 58%) in bonus pools.',
        department: 'Senior Management',
        gapPercentage: 11.8,
        priority: 'high',
        dueDate: '2025-12-20',
        status: 'in_progress',
      },
    ],

    aiInsights: {
      headline: 'Two structural drivers account for the headline 7.1% disparity. Article 10 joint pay assessment obligations are engaged for Sales (Field) and Senior Management.',
      body: 'The field-sales commission framework produces a 10.4% mean variance, traceable to unequal allocation of high-yield territories rather than individual performance. Senior management long-term incentive grants exhibit a 9.1% mean delta concentrated in a 2024 external-hire cohort recruited into functions where variable compensation dominates total reward. Quartile distribution corroborates the diagnosis: female workers represent 64% of Q1 and only 31% of Q4, indicating vertical segregation of 33 percentage points. The 60-basis-point widening over the trailing twelve months reflects an active causal mechanism rather than residual variance, requiring intervention at policy level.',
      recommendations: [
        'Initiate the Article 10 joint pay assessment for Sales (Field) within 60 days. Failure to commence within statutory timeframe may engage penalty provisions under Article 23.',
        'Conduct a structural audit of the commission plan design. The 41% versus 58% participation-rate disparity in variable components suggests indirect discrimination under Article 4, not performance variance.',
        'Commission an independent review of 2024–2025 senior hire and promotion decisions for evidence of bias in starting compensation calibration.',
        'Establish a remediation reserve of €2.1M–€3.4M for structural pay adjustments. Final quantum subject to joint assessment outcomes and worker representative consultation.',
        'Prepare disclosure materials for the national monitoring body in advance of the Article 9 reporting deadline.',
      ],
    },
    aiInsightsDe: {
      headline: 'Zwei strukturelle Treiber erklären die Diskrepanz von 7,1 %. Pflichten zur gemeinsamen Entgeltbewertung nach Artikel 10 sind für Sales (Field) und Senior Management ausgelöst.',
      body: 'Das Provisionssystem im Außendienst weist einen mittleren Variationswert von 10,4 % auf, der auf die ungleiche Zuteilung umsatzstarker Vertriebsgebiete und nicht auf individuelle Leistungen zurückzuführen ist. Die langfristige Anreizvergütung im Senior Management zeigt ein mittleres Delta von 9,1 %, konzentriert auf eine 2024 extern eingestellte Kohorte in Funktionen, in denen variable Vergütung die Gesamtvergütung dominiert. Die Quartilverteilung bestätigt die Diagnose: Frauen stellen 64 % von Q1 und nur 31 % von Q4, eine vertikale Segregation von 33 Prozentpunkten. Die Ausweitung um 60 Basispunkte über die letzten zwölf Monate weist auf einen aktiven kausalen Mechanismus und nicht auf residuale Varianz hin und erfordert ein Eingreifen auf Policy-Ebene.',
      recommendations: [
        'Die gemeinsame Entgeltbewertung nach Artikel 10 für Sales (Field) innerhalb von 60 Tagen einleiten. Bei Nichteinhaltung der gesetzlichen Frist können Sanktionsregelungen nach Artikel 23 greifen.',
        'Eine strukturelle Prüfung der Provisionsplanung durchführen. Die Differenz von 41 % gegenüber 58 % bei der Teilhabe an variabler Vergütung deutet auf mittelbare Diskriminierung nach Artikel 4 und nicht auf leistungsbezogene Varianz hin.',
        'Eine unabhängige Überprüfung der Senior-Hires und Beförderungsentscheidungen 2024–2025 auf Hinweise einer Voreingenommenheit bei der Festlegung der Einstiegsvergütung beauftragen.',
        'Eine Sanierungsrückstellung von 2,1 Mio. € bis 3,4 Mio. € für strukturelle Vergütungsanpassungen einrichten. Der finale Betrag hängt von den Ergebnissen der gemeinsamen Bewertung und der Konsultation der Arbeitnehmervertretung ab.',
        'Offenlegungsmaterialien für die nationale Überwachungsstelle vor Ablauf der Berichtsfrist nach Artikel 9 vorbereiten.',
      ],
    },
  },

  'global-bank-es': {
    id: 'global-bank-es',
    name: 'Global Bank',
    shortName: 'Global Bank ES',
    country: 'Spain',
    countryCode: 'ES',
    industry: 'Financial Services',
    employees: 41600,
    narrativeRole: 'scale',
    narrativeTagline: 'Complex multi-entity structure, 41,600 employees across 18 legal entities.',
    reportingYear: 2025,

    metrics: {
      meanPayGap: 5.8,
      meanPayGapDelta: -0.2,
      medianPayGap: 4.3,
      medianPayGapDelta: -0.1,
      variableMeanGap: 9.2,
      variableMedianGap: 6.8,
      variableRecipientsFemale: 82,
      variableRecipientsMale: 87,
      complianceScore: 72,
      activeAssessments: 3,
      totalWorkers: 41600,
      femaleShare: 53.1,
    },

    categoriesOfWorkers: [
      { name: 'C-Suite & Board',       meanGap: 6.2, medianGap: 4.8, headcount: 38,    femaleShare: 29 },
      { name: 'Managing Directors',    meanGap: 7.4, medianGap: 5.9, headcount: 284,   femaleShare: 26 },
      { name: 'Directors',             meanGap: 6.8, medianGap: 5.1, headcount: 1240,  femaleShare: 34 },
      { name: 'VPs (Investment Bank)', meanGap: 8.1, medianGap: 6.4, headcount: 2180,  femaleShare: 31 },
      { name: 'VPs (Retail & Commercial)', meanGap: 4.2, medianGap: 3.1, headcount: 3420, femaleShare: 48 },
      { name: 'Associates',            meanGap: 5.4, medianGap: 4.0, headcount: 6800,  femaleShare: 46 },
      { name: 'Analysts',              meanGap: 3.8, medianGap: 2.6, headcount: 5200,  femaleShare: 52 },
      { name: 'Branch Network',        meanGap: 5.1, medianGap: 3.9, headcount: 14200, femaleShare: 61 },
      { name: 'Operations & Tech',     meanGap: 6.4, medianGap: 4.8, headcount: 8238,  femaleShare: 44 },
    ],

    quartiles: [
      { name: 'Q1 (Lowest)',  femalePct: 61, malePct: 39, headcount: 10400 },
      { name: 'Q2',           femalePct: 56, malePct: 44, headcount: 10400 },
      { name: 'Q3',           femalePct: 50, malePct: 50, headcount: 10400 },
      { name: 'Q4 (Highest)', femalePct: 36, malePct: 64, headcount: 10400 },
    ],

    trendData: trend(6.2, 5.8, 0.25),

    assessmentAlerts: [
      {
        id: 'ASS-2025-Q4-042',
        title: 'Joint Pay Assessment, Investment Banking VPs',
        description: '8.1% mean gap sustained over 7 months in Investment Banking VP grade. Joint pay assessment required under Art. 10.',
        department: 'VPs (Investment Bank)',
        gapPercentage: 8.1,
        priority: 'critical',
        dueDate: '2026-01-30',
        status: 'in_progress',
      },
      {
        id: 'ASS-2025-Q4-043',
        title: 'Managing Director Compensation Review',
        description: 'Mean gap of 7.4% with low female representation (26%) suggests structural rather than individual pay issues.',
        department: 'Managing Directors',
        gapPercentage: 7.4,
        priority: 'high',
        dueDate: '2026-02-15',
        status: 'pending',
      },
      {
        id: 'ASS-2025-Q4-044',
        title: 'Variable Compensation Audit, Investment Bank',
        description: 'Variable-pay gap of 9.2% paired with 5-point difference in bonus participation rates.',
        department: 'Investment Banking',
        gapPercentage: 9.2,
        priority: 'medium',
        dueDate: '2026-03-01',
        status: 'scheduled',
      },
    ],

    aiInsights: {
      headline: 'Aggregate 5.8% gap conceals material divergence across business lines. Investment Banking and Managing Director cohorts both exceed Article 10 threshold.',
      body: 'Retail and Commercial Banking operates at a 4.2% mean gap with near-parity workforce composition, demonstrating that compliant outcomes are achievable within the institutional framework. The Investment Banking division presents a contrasting picture: a sustained 8.1% mean variance at the VP grade, paired with 31% female representation, indicates that observed pay differentials cannot be remediated through individual-level adjustment alone, the underlying pipeline geometry is the constraining factor. Across the eighteen consolidated legal entities, dispersion is substantial; three subsidiaries report under 3% while two exceed 8%. Entity-level reporting under the directive will surface this dispersion publicly.',
      recommendations: [
        'Prioritise the Article 10 joint pay assessment for Investment Banking VPs. The cohort breach is documented and statutory deadlines apply.',
        'Sequence remediation by entity rather than by function. Highest-variance subsidiaries should be addressed first to limit reputational exposure at the disclosure stage.',
        'Commission a Managing Director succession review. The 26% female representation at this grade represents the binding constraint on multi-year gap closure trajectories.',
        'Benchmark Investment Banking compensation philosophy against BBVA and Santander disclosures, anticipated Q2 2026, to inform peer-relative remediation positioning.',
        'Establish quarterly entity-level reporting to the Board Risk and Compliance Committee for the duration of the directive transition period.',
      ],
    },
    aiInsightsDe: {
      headline: 'Der aggregierte Wert von 5,8 % verdeckt eine erhebliche Streuung über die Geschäftsbereiche. Investment Banking VPs und Managing Directors überschreiten beide den Schwellenwert nach Artikel 10.',
      body: 'Das Retail- und Geschäftskundenbanking arbeitet mit einem mittleren Pay-Gap von 4,2 % und nahezu paritätischer Belegschaftszusammensetzung, ein Beleg dafür, dass richtlinienkonforme Ergebnisse innerhalb des institutionellen Rahmens erreichbar sind. Das Investment Banking zeigt ein gegenteiliges Bild: eine anhaltende mittlere Varianz von 8,1 % auf der VP-Ebene in Verbindung mit einem Frauenanteil von 31 % weist darauf hin, dass die beobachteten Vergütungsunterschiede nicht allein durch Anpassungen auf Einzelfallbasis behoben werden können, die zugrundeliegende Pipeline-Geometrie ist der bindende Faktor. Über die achtzehn konsolidierten Rechtseinheiten hinweg ist die Streuung erheblich; drei Tochtergesellschaften berichten unter 3 %, während zwei 8 % überschreiten. Die Berichterstattung auf Einheitsebene unter der Richtlinie wird diese Streuung öffentlich sichtbar machen.',
      recommendations: [
        'Die gemeinsame Entgeltbewertung nach Artikel 10 für Investment Banking VPs priorisieren. Der Verstoß auf Kohortenebene ist dokumentiert; gesetzliche Fristen gelten.',
        'Sanierungsmaßnahmen nach Einheit statt nach Funktion sequenzieren. Tochtergesellschaften mit der höchsten Varianz sollten zuerst angegangen werden, um Reputationsrisiken bei Offenlegung zu begrenzen.',
        'Eine Nachfolgeplanungsüberprüfung für Managing Directors beauftragen. Der Frauenanteil von 26 % auf dieser Ebene stellt die bindende Beschränkung für mehrjährige Schließungspfade dar.',
        'Die Vergütungsphilosophie im Investment Banking gegen die für Q2 2026 erwarteten Offenlegungen von BBVA und Santander benchmarken, um die Sanierungsstrategie peer-relativ zu positionieren.',
        'Eine quartalsweise Berichterstattung auf Einheitsebene an den Vorstandsausschuss für Risiko und Compliance für die Dauer der Übergangsphase der Richtlinie etablieren.',
      ],
    },
  },

  'global-manufacturer-ch': {
    id: 'global-manufacturer-ch',
    name: 'Global Manufacturer',
    shortName: 'Global Manufacturer CH',
    country: 'Switzerland',
    countryCode: 'CH',
    industry: 'Industrial Manufacturing',
    employees: 15820,
    narrativeRole: 'ai-insight',
    narrativeTagline: 'AI identified the pattern: 9.4% gap driven almost entirely by Production vs. R&D division split.',
    reportingYear: 2025,

    metrics: {
      meanPayGap: 9.4,
      meanPayGapDelta: +0.1,
      medianPayGap: 7.2,
      medianPayGapDelta: -0.1,
      variableMeanGap: 14.1,
      variableMedianGap: 10.4,
      variableRecipientsFemale: 34,
      variableRecipientsMale: 61,
      complianceScore: 51,
      activeAssessments: 2,
      totalWorkers: 15820,
      femaleShare: 28.4,
    },

    categoriesOfWorkers: [
      { name: 'Executive Committee',    meanGap: 4.2,  medianGap: 3.1, headcount: 18,   femaleShare: 33 },
      { name: 'R&D Leadership',         meanGap: 3.8,  medianGap: 2.4, headcount: 94,   femaleShare: 41 },
      { name: 'R&D Engineers',          meanGap: 2.9,  medianGap: 1.8, headcount: 1820, femaleShare: 38 },
      { name: 'R&D Technicians',        meanGap: 3.2,  medianGap: 2.1, headcount: 980,  femaleShare: 42 },
      { name: 'Production Leadership',  meanGap: 11.4, medianGap: 9.1, headcount: 142,  femaleShare: 18 },
      { name: 'Production Supervisors', meanGap: 12.8, medianGap: 10.2, headcount: 620, femaleShare: 14 },
      { name: 'Production Line',        meanGap: 10.2, medianGap: 7.8, headcount: 8940, femaleShare: 24 },
      { name: 'Quality & Compliance',   meanGap: 5.1,  medianGap: 3.9, headcount: 1240, femaleShare: 48 },
      { name: 'Corporate Functions',    meanGap: 4.8,  medianGap: 3.4, headcount: 1966, femaleShare: 58 },
    ],

    quartiles: [
      { name: 'Q1 (Lowest)',  femalePct: 42, malePct: 58, headcount: 3955 },
      { name: 'Q2',           femalePct: 31, malePct: 69, headcount: 3955 },
      { name: 'Q3',           femalePct: 22, malePct: 78, headcount: 3955 },
      { name: 'Q4 (Highest)', femalePct: 18, malePct: 82, headcount: 3955 },
    ],

    trendData: trend(9.3, 9.4, 0.35),

    assessmentAlerts: [
      {
        id: 'ASS-2025-Q4-008',
        title: 'Joint Pay Assessment, Production Division',
        description: 'Mean gap of 10.2–12.8% across all Production categories, sustained for 11+ months. Art. 10 joint pay assessment mandatory.',
        department: 'Production (All Grades)',
        gapPercentage: 10.2,
        priority: 'critical',
        dueDate: '2025-12-31',
        status: 'in_progress',
      },
      {
        id: 'ASS-2025-Q4-009',
        title: 'Variable Comp Structure Review, Production',
        description: 'Variable-component gap of 14.1% with only 34% of female workers receiving variable pay vs 61% male.',
        department: 'Production',
        gapPercentage: 14.1,
        priority: 'high',
        dueDate: '2026-01-20',
        status: 'pending',
      },
    ],

    aiInsights: {
      headline: 'The 9.4% enterprise-level disparity is functionally a Production Division phenomenon. R&D and Corporate Functions both operate within compliant ranges.',
      body: 'Diagnostic decomposition produces an unusually clean signal. R&D Engineers report a 2.9% mean gap; Corporate Functions sits at 4.8%; both are within compliant tolerance. The Production Division, by contrast, exhibits a consistent 10.2–12.8% gap across every grade, with the variance progressing rather than diminishing at higher seniority bands. Three causal mechanisms are evident: (i) the shift-premium eligibility structure systematically advantages roles with male-skewed staffing patterns, constituting probable indirect discrimination under Article 4; (ii) female representation declines monotonically by quartile, from 42% in Q1 to 18% in Q4, indicating compounding vertical segregation; (iii) the production-output-linked variable component framework correlates with shift availability rather than productivity per se.',
      recommendations: [
        'Confine remediation to the Production Division. Enterprise-wide interventions would dilute resources and address symptoms outside the causal locus.',
        'Commission an Article 4 indirect discrimination review of the shift-premium eligibility framework. Legal exposure is non-trivial; engage external employment counsel.',
        'Production Leadership pipeline restructuring is the binding constraint. Current 14–18% female representation at supervisor-and-above grades cannot mathematically support the gap closure trajectory required by 2030.',
        'Adopt R&D compensation governance practices as the internal benchmark. The methodology has been demonstrated effective within the same legal and tax environment.',
        'Re-baseline the variable-component framework to decouple eligibility from shift-availability proxies. Anticipated impact: 200–400 basis point reduction within 18 months.',
      ],
    },
    aiInsightsDe: {
      headline: 'Die unternehmensweite Diskrepanz von 9,4 % ist funktional ein Phänomen der Produktionsdivision. F&E sowie Konzernfunktionen arbeiten beide innerhalb richtlinienkonformer Bereiche.',
      body: 'Die diagnostische Zerlegung liefert ein ungewöhnlich klares Signal. F&E-Ingenieure berichten einen mittleren Pay-Gap von 2,9 %; Konzernfunktionen liegen bei 4,8 %; beide bewegen sich innerhalb richtlinienkonformer Toleranzen. Die Produktionsdivision hingegen weist einen konsistenten Pay-Gap von 10,2 bis 12,8 % über alle Stufen hinweg auf, wobei die Varianz auf höheren Senioritätsbändern eher zunimmt als sich verringert. Drei kausale Mechanismen sind erkennbar: (i) die Anspruchsstruktur der Schichtprämien begünstigt systematisch Rollen mit männerdominierter Personalausstattung, was eine wahrscheinliche mittelbare Diskriminierung nach Artikel 4 darstellt; (ii) der Frauenanteil sinkt monoton nach Quartil, von 42 % in Q1 auf 18 % in Q4, und weist auf eine sich verstärkende vertikale Segregation hin; (iii) der an Produktionskennzahlen gekoppelte variable Vergütungsrahmen korreliert eher mit Schichtverfügbarkeit als mit Produktivität als solcher.',
      recommendations: [
        'Sanierungsmaßnahmen auf die Produktionsdivision begrenzen. Unternehmensweite Interventionen würden Ressourcen verteilen und Symptome außerhalb des kausalen Ursprungs adressieren.',
        'Eine Überprüfung der Schichtprämien-Anspruchsregelung auf mittelbare Diskriminierung nach Artikel 4 beauftragen. Das Haftungsrisiko ist nicht trivial; externe Arbeitsrechtsberatung einschalten.',
        'Die Restrukturierung der Pipeline für Produktionsleitung ist der bindende Faktor. Der derzeitige Frauenanteil von 14 bis 18 % auf Supervisor-Ebene und höher kann mathematisch nicht den bis 2030 erforderlichen Schließungspfad tragen.',
        'Die Vergütungsgovernance-Praktiken aus F&E als internen Benchmark übernehmen. Die Methodik wurde im selben rechtlichen und steuerlichen Umfeld nachweislich wirksam erprobt.',
        'Den variablen Vergütungsrahmen neu kalibrieren, um die Anspruchsberechtigung von Schichtverfügbarkeits-Proxies zu entkoppeln. Erwartete Wirkung: Reduzierung um 200 bis 400 Basispunkte innerhalb von 18 Monaten.',
      ],
    },
  },

  'global-security-uk': {
    id: 'global-security-uk',
    name: 'Global Security Services',
    shortName: 'Global Security UK',
    country: 'United Kingdom',
    countryCode: 'GB',
    industry: 'Security & Facilities Services',
    employees: 24700,
    narrativeRole: 'international',
    narrativeTagline: 'Non-EU, preparing for UK equivalent, proof ClearEqual scales beyond the Directive.',
    reportingYear: 2025,

    metrics: {
      meanPayGap: 4.1,
      meanPayGapDelta: -0.8,
      medianPayGap: 2.9,
      medianPayGapDelta: -0.6,
      variableMeanGap: 6.2,
      variableMedianGap: 4.1,
      variableRecipientsFemale: 54,
      variableRecipientsMale: 61,
      complianceScore: 86,
      activeAssessments: 1,
      totalWorkers: 24700,
      femaleShare: 31.8,
    },

    categoriesOfWorkers: [
      { name: 'Senior Leadership',    meanGap: 5.2, medianGap: 3.8, headcount: 62,    femaleShare: 32 },
      { name: 'Regional Management',  meanGap: 4.8, medianGap: 3.4, headcount: 184,  femaleShare: 28 },
      { name: 'Operations Management',meanGap: 4.4, medianGap: 3.1, headcount: 840,  femaleShare: 24 },
      { name: 'Site Supervisors',     meanGap: 3.8, medianGap: 2.6, headcount: 2420, femaleShare: 22 },
      { name: 'Security Officers',    meanGap: 3.2, medianGap: 2.1, headcount: 16200, femaleShare: 28 },
      { name: 'Technical Specialists',meanGap: 5.4, medianGap: 4.2, headcount: 1420, femaleShare: 38 },
      { name: 'Corporate & Support',  meanGap: 3.8, medianGap: 2.4, headcount: 3574, femaleShare: 62 },
    ],

    quartiles: [
      { name: 'Q1 (Lowest)',  femalePct: 48, malePct: 52, headcount: 6175 },
      { name: 'Q2',           femalePct: 34, malePct: 66, headcount: 6175 },
      { name: 'Q3',           femalePct: 24, malePct: 76, headcount: 6175 },
      { name: 'Q4 (Highest)', femalePct: 22, malePct: 78, headcount: 6175 },
    ],

    trendData: trend(4.9, 4.1, 0.18),

    assessmentAlerts: [
      {
        id: 'ASS-2025-Q4-022',
        title: 'Technical Specialists, Preventive Review',
        description: '5.4% mean gap in Technical Specialists grade, within compliant range but worth monitoring as UK regulations evolve.',
        department: 'Technical Specialists',
        gapPercentage: 5.4,
        priority: 'medium',
        dueDate: '2026-02-28',
        status: 'scheduled',
      },
    ],

    aiInsights: {
      headline: 'Mean gap of 4.1% reflects active remediation. Trajectory and policy maturity position the organisation favourably for anticipated UK regulatory tightening.',
      body: 'The 80-basis-point contraction over twelve months represents the most rapid improvement among comparable enterprises in the reference dataset. Causality is attributable to a deliberate intervention: the 2024 reform of shift-allowance eligibility addressed an indirect discrimination pattern structurally analogous to those still present in continental industrial sectors. Pay-for-role parity is now substantively achieved within the workforce. The residual constraint is representational rather than compensational: 22% female representation in Q4 indicates that further headline gap reduction is principally a function of pipeline and progression policy, not pay calibration. Technical Specialists grade (5.4% mean) is the only category warranting continued observation under existing frameworks.',
      recommendations: [
        'Pursue voluntary external publication of pay equity metrics in advance of UK regulatory mandates. First-mover positioning has measurable reputational and talent-acquisition benefits.',
        'Codify the 2024 shift-allowance remediation methodology as an internal case study. The framework is transferable to continental European operations entering the EU directive transition period.',
        'Maintain an annual review cycle for the Technical Specialists grade. Current trajectory does not require structural intervention but the 5.4% threshold proximity warrants documentation.',
        'Initiate scenario modelling for EU Directive 2023/970 application to continental subsidiaries. Anticipate transposition variance across Member States; Germany and France will likely impose the most stringent reporting cadence.',
        'Engage with HM Treasury and EHRC consultations on UK pay transparency reform. Industry-leading data positions the organisation to influence final regulatory design.',
      ],
    },
    aiInsightsDe: {
      headline: 'Der mittlere Pay-Gap von 4,1 % spiegelt aktive Sanierungsarbeit wider. Verlauf und Reife der Policy positionieren das Unternehmen günstig für die zu erwartende Verschärfung der UK-Regulierung.',
      body: 'Die Verringerung um 80 Basispunkte über zwölf Monate stellt die schnellste Verbesserung unter vergleichbaren Unternehmen im Referenzdatensatz dar. Die Kausalität ist auf eine bewusste Intervention zurückzuführen: Die Reform der Schichtzulagen-Anspruchsregelung 2024 hat ein mittelbares Diskriminierungsmuster behoben, das strukturell jenen ähnelt, die in kontinentaleuropäischen Industriesektoren noch bestehen. Pay-for-Role-Parität ist innerhalb der Belegschaft inhaltlich erreicht. Die verbleibende Beschränkung ist repräsentativer und nicht kompensatorischer Natur: Der Frauenanteil von 22 % in Q4 bedeutet, dass eine weitere Verringerung des Headline-Pay-Gaps in erster Linie eine Funktion der Pipeline- und Aufstiegspolitik und nicht der Gehaltskalibrierung ist. Die Stufe Technical Specialists (Mittelwert 5,4 %) ist die einzige Kategorie, die unter den bestehenden Rahmenbedingungen weiterhin Beobachtung verdient.',
      recommendations: [
        'Eine freiwillige externe Veröffentlichung der Pay-Equity-Kennzahlen vor den UK-Regulierungsvorschriften anstreben. First-Mover-Positionierung hat messbare Reputations- und Personalakquisevorteile.',
        'Die Sanierungsmethodik der Schichtzulagen 2024 als internen Best-Practice-Fall kodifizieren. Der Rahmen ist auf kontinentaleuropäische Geschäftsbereiche übertragbar, die in die Übergangsphase der EU-Richtlinie eintreten.',
        'Einen jährlichen Überprüfungszyklus für die Stufe Technical Specialists beibehalten. Der aktuelle Verlauf erfordert keine strukturelle Intervention, doch die Nähe zum 5,4-%-Schwellenwert rechtfertigt Dokumentation.',
        'Die Szenario-Modellierung für die Anwendung der EU-Richtlinie 2023/970 auf kontinentaleuropäische Tochtergesellschaften einleiten. Mit Umsetzungsvarianz zwischen den Mitgliedstaaten rechnen; Deutschland und Frankreich werden voraussichtlich die strengsten Berichtsrhythmen vorgeben.',
        'An den Konsultationen von HM Treasury und EHRC zur UK-Pay-Transparency-Reform mitwirken. Branchenführende Daten ermöglichen es dem Unternehmen, die finale Regulierungsausgestaltung zu beeinflussen.',
      ],
    },
  },
};

export const TENANT_LIST = Object.values(TENANTS);

export const DEFAULT_TENANT_ID = 'clearequal-demo';

export const getTenant = (id) => TENANTS[id] || TENANTS[DEFAULT_TENANT_ID];
